import { useState, useEffect } from 'react';
import { Sparkles, AlertCircle, Loader2, ChevronDown, ChevronUp, Save, History } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { generateIssueSummary } from '@/lib/openai';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import type { ReportedIssue } from '@/types';

interface AISummaryProps {
  issues: ReportedIssue[];
  isFiltered: boolean;
  filters?: Record<string, any>;
}

export function AISummary({ issues, isFiltered, filters }: AISummaryProps) {
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [remainingCount, setRemainingCount] = useState<number | null>(null);
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [summaryName, setSummaryName] = useState('');
  const [savedSummaries, setSavedSummaries] = useState<any[]>([]);
  const [savedSummariesOpen, setSavedSummariesOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recentSummaries, setRecentSummaries] = useState<any[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);

  // Fetch recent summaries on mount
  useEffect(() => {
    fetchRecentSummaries();
  }, []);

  const fetchRecentSummaries = async () => {
    setIsLoadingRecent(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data, error } = await supabase
        .from('sms_ai_summaries')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setRecentSummaries(data || []);
    } catch (error) {
      console.error('Error fetching recent summaries:', error);
    } finally {
      setIsLoadingRecent(false);
    }
  };

  // Fetch saved summaries when opening the dialog
  const handleOpenSavedSummaries = () => {
    fetchSavedSummaries();
    setSavedSummariesOpen(true);
  };

  const fetchSavedSummaries = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('You must be logged in to view saved summaries');
      }

      const { data, error } = await supabase
        .from('sms_ai_summaries')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedSummaries(data || []);
    } catch (error) {
      console.error('Error fetching saved summaries:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to fetch saved summaries');
    }
  };

  const handleSaveSummary = async () => {
    if (!summary || !summaryName.trim()) return;
    
    setIsSaving(true);
    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('You must be logged in to save summaries');
      }

      // Prepare filters data
      const filterData = filters ? {
        search: filters.search || null,
        status: filters.status !== 'all' ? filters.status : null,
        school_id: filters.school_id || null,
        grade: filters.grade || null,
        date: filters.date ? format(filters.date, 'yyyy-MM-dd') : null
      } : null;

      const { error } = await supabase
        .from('sms_ai_summaries')
        .insert({
          user_id: session.user.id,
          summary,
          issue_count: issues.length,
          name: summaryName.trim(),
          filters: filterData
        });

      if (error) throw error;
      setSaveDialogOpen(false);
      setSummaryName('');
      toast.success('Summary saved successfully');
      await Promise.all([
        fetchSavedSummaries(),
        fetchRecentSummaries()
      ]); // Refresh both lists
    } catch (error) {
      console.error('Error saving summary:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save summary');
    } finally {
      setIsSaving(false);
    }
  };

  const loadSavedSummary = (savedSummary: any) => {
    setSummary(savedSummary.summary);
    setSavedSummariesOpen(false);
  };

  const generateSummary = async () => {
    setIsLoading(true);
    setError(null);
    setSummary(null);

    try {
      const issuesToAnalyze = issues.slice(0, 10);
      const { summary, remainingCount } = await generateIssueSummary(issuesToAnalyze);
      
      // Format the summary by replacing markdown-style formatting
      const formattedSummary = summary
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers
        .replace(/##\s*(.*?)(?:\n|$)/g, '$1') // Remove heading markers
        .replace(/\n\s*-\s*/g, '\n• '); // Replace dashes with bullets

      setSummary(formattedSummary);
      setRemainingCount(remainingCount);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to generate summary';

      if (errorMessage.includes('quota') || errorMessage === 'QUOTA_EXCEEDED') {
        setIsQuotaExceeded(true);
        setError('AI analysis is temporarily unavailable due to high demand. Please try again later.');
        return;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isFiltered || issues.length === 0) {
    return null;
  }

  return (
    <Card className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-lg text-blue-900">AI Analysis</CardTitle>
        </div>
        {typeof remainingCount === 'number' && (
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenSavedSummaries}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
            >
              <History className="h-4 w-4 mr-1" />
              View All Summaries
            </Button>
            <span className="text-sm text-blue-600">
              {remainingCount} summaries remaining today
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Recent Summaries Section */}
        {recentSummaries.length > 0 && !summary && !isLoading && !error && (
          <div className="mb-6 border-b border-blue-200 pb-6">
            <h3 className="text-sm font-medium text-blue-900 mb-3">Recent Summaries</h3>
            <div className="space-y-3">
              {recentSummaries.map((recent) => (
                <div
                  key={recent.id}
                  className="p-3 border border-blue-200 rounded-lg hover:bg-blue-50/50 cursor-pointer transition-colors"
                  onClick={() => loadSavedSummary(recent)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-sm">{recent.name}</h4>
                    <span className="text-xs text-blue-600">
                      {format(new Date(recent.created_at), 'MMM d, HH:mm')}
                    </span>
                  </div>
                  <p className="text-xs text-blue-700 line-clamp-2">
                    {recent.summary}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!summary && !isLoading && !error && (
          <div className="text-center py-6">
            <p className="text-sm text-blue-700 mb-4">
              Generate an AI-powered analysis of the current issues to identify patterns and trends.
              {issues.length > 10 && " The analysis will focus on the 10 most recent issues."}
            </p>
            <Button
              onClick={generateSummary}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={isQuotaExceeded}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Generate Analysis
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {summary && (
          <div className="space-y-4">
            <div className={`prose prose-sm max-w-none transition-all duration-200 ${isCollapsed ? 'max-h-40' : 'max-h-[1000px]'} overflow-hidden`}>
              {summary.split('\n').map((paragraph, index) => {
                // Check if the paragraph is a heading (first line or after double newline)
                const isHeading = index === 0 || (index > 0 && summary.split('\n')[index - 1] === '');
                
                if (paragraph.trim() === '') return null;
                
                return (
                  <p 
                    key={index} 
                    className={`${
                      isHeading 
                        ? 'font-semibold text-blue-900 text-lg mt-4' 
                        : 'text-blue-800'
                    }`}
                  >
                    {paragraph}
                  </p>
                );
              })}
            </div>
            
            <div className="flex items-center justify-between pt-2 border-t border-blue-200">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
              >
                {isCollapsed ? (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Show More
                  </>
                ) : (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Show Less
                  </>
                )}
              </Button>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSaveDialogOpen(true)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateSummary}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  disabled={isLoading || isQuotaExceeded}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Regenerate
                </Button>
              </div>
            </div>

            {issues.length > 10 && (
              <p className="text-sm text-blue-600 italic">
                * This analysis is based on the 10 most recent reported issues.
              </p>
            )}
          </div>
        )}

        {/* Save Summary Dialog */}
        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save Summary</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={summaryName}
                  onChange={(e) => setSummaryName(e.target.value)}
                  placeholder="Enter a name for this summary"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setSaveDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSummary}
                disabled={!summaryName.trim() || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Summary'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Saved Summaries Dialog */}
        <Dialog open={savedSummariesOpen} onOpenChange={setSavedSummariesOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Saved Summaries</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {savedSummaries.length === 0 ? (
                <p className="text-center text-muted-foreground">
                  No saved summaries yet
                </p>
              ) : (
                savedSummaries.map((saved) => (
                  <div
                    key={saved.id}
                    className="p-4 border rounded-lg hover:bg-accent/5 cursor-pointer"
                    onClick={() => loadSavedSummary(saved)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium">{saved.name}</h3>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(saved.created_at), 'PPp')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {saved.summary}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <span>{saved.issue_count} issues analyzed</span>
                      {saved.filters && (
                        <span>• Filtered analysis</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}